import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { BreakdownHierarchyNode, BreakdownStatus } from '../../services/breakdown-api.js';

const STATUS_LABELS: Record<string, string> = {
  open: 'Offen',
  in_progress: 'In Arbeit',
  done: 'Erledigt',
  skipped: 'Uebersprungen',
};

const STATUS_CYCLE: BreakdownStatus[] = ['open', 'in_progress', 'done'];

@customElement('cp-goal-card')
export class CpGoalCard extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) goal!: BreakdownHierarchyNode;
  @property({ type: Boolean }) expandable = false;
  @property({ type: Boolean }) expanded = false;
  @property({ type: Boolean, attribute: 'is-child' }) isChild = false;

  private _onGoalClick(): void {
    if (!this.expandable) return;
    this.dispatchEvent(
      new CustomEvent('goal-click', { detail: { goalId: this.goal.id }, bubbles: true, composed: true }),
    );
  }

  private _onEditClick(e: Event): void {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('edit-click', { detail: { goalId: this.goal.id }, bubbles: true, composed: true }),
    );
  }

  private _onStatusClick(e: Event): void {
    e.stopPropagation();
    const currentIndex = STATUS_CYCLE.indexOf(this.goal.status);
    const nextStatus = STATUS_CYCLE[(currentIndex + 1) % STATUS_CYCLE.length];
    this.dispatchEvent(
      new CustomEvent('status-change', {
        detail: { goalId: this.goal.id, status: nextStatus },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _childrenDone(): number {
    return this.goal.children.filter(c => c.status === 'done' || c.status === 'skipped').length;
  }

  render() {
    const statusClass = `cp-goal-card__badge--${this.goal.status.replace('_', '-')}`;
    const statusLabel = STATUS_LABELS[this.goal.status] ?? this.goal.status;

    return html`
      <div
        class="cp-goal-card ${this.expandable ? 'cp-goal-card--expandable' : ''} ${this.expanded ? 'cp-goal-card--expanded' : ''}"
        @click=${this._onGoalClick}
      >
        <div class="cp-goal-card__content">
          <div class="cp-goal-card__header">
            <span class="cp-goal-card__title">${this.isChild && this.goal.description ? this.goal.description : this.goal.title}</span>
            <button
              class="cp-goal-card__edit-btn"
              @click=${this._onEditClick}
              title="Ziel bearbeiten"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
              </svg>
            </button>
          </div>
          ${!this.isChild && this.goal.description ? html`
            <span class="cp-goal-card__description">${this.goal.description}</span>
          ` : ''}
          <div class="cp-goal-card__footer">
            <button
              class="cp-goal-card__badge cp-goal-card__badge--clickable ${statusClass}"
              @click=${this._onStatusClick}
              title="Status aendern"
            >${statusLabel}</button>
            ${this.expandable && this.goal.children.length > 0 ? html`
              <span class="cp-goal-card__children-progress">
                <span class="cp-goal-card__children-bar">
                  <span class="cp-goal-card__children-fill" style="width: ${Math.round((this._childrenDone() / this.goal.children.length) * 100)}%"></span>
                </span>
                <span class="cp-goal-card__children-count">${this._childrenDone()}/${this.goal.children.length}</span>
              </span>
            ` : ''}
            ${this.expandable ? html`
              <span class="cp-goal-card__chevron ${this.expanded ? 'cp-goal-card__chevron--open' : ''}">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </span>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-goal-card': CpGoalCard;
  }
}
